import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCaretDown,
  faEllipsisVertical,
  faFileExport,
  faFileImport,
  faFolderOpen,
  faMagnifyingGlass,
  faPen,
  faPlus,
  faRotate,
  faShareNodes,
  faTableCellsLarge,
  faTableList,
  faGlobe,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import {
  deleteLibraryLesson,
  getCurriculumExport,
  getLessonInspiration,
  getLibraryLessons,
  patchTeacherLesson,
  postCurriculumImport,
  postSubmitLessonToCommunity,
  postUpgradeAllLessons,
  postUpgradeLesson,
  type CurriculumBundleV1,
  type InspirationInfo,
  type TeacherLessonRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { fuzzyFilter } from "./fuzzySearch";
import { buildSoftDeleteLessonConfirmMessage } from "./lessonDeleteMessages";
import { LessonLibraryCard } from "./LessonLibraryCard";
import { LessonIcon } from "./LessonIcon";
import { FloatingActionsMenu } from "./FloatingActionsMenu";
import { isAdvancedLessonMode } from "./lessonAdvancedVisual";
import {
  LESSON_LIBRARY_SORT_OPTIONS,
  sortLessonLibrary,
  type LessonLibrarySortKey,
} from "./lessonLibrarySort";
import { useUrlState } from "./useUrlState";
import { InspirationModal } from "./InspirationModal";

type ViewMode = "cards" | "table";
type ModeFilter = "all" | "simple" | "advanced" | "inspired" | "authored" | "published";

function publishStatusLabel(status?: string) {
  if (status === "approved") {
    return (
      <span className="teacher-pill teacher-pill--published-badge">
        <FontAwesomeIcon icon={faGlobe} fixedWidth aria-hidden />
        Published
      </span>
    );
  }
  if (status === "pending_review") {
    return <span className="teacher-pill teacher-pill--pending">In review</span>;
  }
  if (status === "rejected") {
    return <span className="teacher-pill teacher-pill--pending">Changes requested</span>;
  }
  return null;
}

/** Card overlay: only show a badge when the lesson is live in the community. */
function cardPublishedBadge(status?: string) {
  if (status !== "approved") return null;
  return (
    <span className="teacher-pill teacher-pill--published-badge">
      <FontAwesomeIcon icon={faGlobe} fixedWidth aria-hidden />
      Published
    </span>
  );
}

import { lessonCanMoveToDrafts } from "./lessonDraftRules";

type LessonRowMenuProps = {
  readonly lesson: TeacherLessonRecord;
  readonly open: boolean;
  readonly variant?: "card" | "table";
  readonly scope: "library" | "drafts";
  readonly onToggle: () => void;
  readonly onClose: () => void;
  readonly onPublish: () => void;
  readonly onDelete: () => void;
  readonly onInspiration: () => void;
  readonly onMoveToDrafts?: () => void;
  readonly onMoveToLibrary?: () => void;
};

function LessonRowMenu({
  lesson,
  open,
  variant = "table",
  scope,
  onToggle,
  onClose,
  onPublish,
  onDelete,
  onInspiration,
  onMoveToDrafts,
  onMoveToLibrary,
}: LessonRowMenuProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const published = lesson.communityPublishStatus === "approved";
  const communityLocked = !lessonCanMoveToDrafts(lesson);
  const inspired = Boolean(lesson.sourceCommunityLessonId);

  return (
    <div className={`teacher-row-menu${variant === "card" ? " teacher-row-menu--card" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className={variant === "card" ? "teacher-lesson-card-tool" : "btn secondary tight teacher-icon-btn"}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Lesson actions"
        aria-label={`Actions for ${lesson.title}`}
        onClick={(ev) => {
          ev.stopPropagation();
          onToggle();
        }}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </button>
      <FloatingActionsMenu anchorRef={triggerRef} open={open} onClose={onClose}>
          {variant === "table" ? (
            <Link
              to={`/teacher/lessons/${lesson.id}`}
              role="menuitem"
              className="teacher-actions-menu-item"
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faPen} className="teacher-actions-menu-icon" />
              Edit
            </Link>
          ) : null}
          {scope === "drafts" && onMoveToLibrary ? (
            <button type="button" role="menuitem" className="teacher-actions-menu-item" onClick={onMoveToLibrary}>
              <FontAwesomeIcon icon={faFolderOpen} className="teacher-actions-menu-icon" />
              Move to library
            </button>
          ) : null}
          {scope === "library" && onMoveToDrafts && !communityLocked ? (
            <button type="button" role="menuitem" className="teacher-actions-menu-item" onClick={onMoveToDrafts}>
              <FontAwesomeIcon icon={faFolderOpen} className="teacher-actions-menu-icon" />
              Move to drafts
            </button>
          ) : null}
          {scope === "library" ? (
            <button type="button" role="menuitem" className="teacher-actions-menu-item" onClick={onPublish}>
              <FontAwesomeIcon icon={faShareNodes} className="teacher-actions-menu-icon" />
              {published ? "Update in community" : "Publish to community"}
            </button>
          ) : null}
          {inspired ? (
            <button
              type="button"
              role="menuitem"
              className="teacher-actions-menu-item"
              onClick={onInspiration}
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="teacher-actions-menu-icon" />
              View inspiration
            </button>
          ) : null}
          <div className="teacher-actions-menu-divider" />
          <button
            type="button"
            role="menuitem"
            className="teacher-actions-menu-item teacher-actions-menu-item--danger"
            onClick={onDelete}
          >
            <FontAwesomeIcon icon={faTrash} className="teacher-actions-menu-icon" />
            Delete
          </button>
      </FloatingActionsMenu>
    </div>
  );
}

export default function TeacherLessonLibraryPage({
  embedded = false,
  scope = "library",
  onLibraryChanged,
}: {
  readonly embedded?: boolean;
  readonly scope?: "library" | "drafts";
  readonly onLibraryChanged?: () => void;
}) {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState<TeacherLessonRecord[]>([]);
  const [search, setSearch] = useUrlState("q", "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [rowMenuId, setRowMenuId] = useState<string | null>(null);
  const [viewRaw, setView] = useUrlState("view", "cards");
  const [modeRaw, setModeFilter] = useUrlState("filter", "all");
  const [sortRaw, setSortKey] = useUrlState("sort", "title");
  const [upgradeReport, setUpgradeReport] = useState<
    { lessonId: string; title: string; toVersion: number }[] | null
  >(null);
  const [upgradingAll, setUpgradingAll] = useState(false);
  const [inspiration, setInspiration] = useState<{
    lessonId: string;
    info: InspirationInfo | null;
    loading: boolean;
    upgrading: boolean;
  } | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  const viewMode: ViewMode = viewRaw === "table" ? "table" : "cards";
  const modeFilter = (["all", "simple", "advanced", "inspired", "authored", "published"].includes(modeRaw)
    ? modeRaw
    : "all") as ModeFilter;
  const sortKey = (LESSON_LIBRARY_SORT_OPTIONS.some((o) => o.key === sortRaw)
    ? sortRaw
    : "title") as LessonLibrarySortKey;

  const refresh = useCallback(async () => {
    if (!bearer) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getLibraryLessons(bearer, {
        limit: 200,
        ...(scope === "drafts" ? { drafts: true } : {}),
      });
      setLessons(data.lessons);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load lessons");
      setLessons([]);
    } finally {
      setLoading(false);
    }
  }, [bearer, scope]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const byMode = lessons.filter((l) => {
      switch (modeFilter) {
        case "simple":
          return !isAdvancedLessonMode(l.workspaceMode);
        case "advanced":
          return isAdvancedLessonMode(l.workspaceMode);
        case "inspired":
          return Boolean(l.sourceCommunityLessonId);
        case "authored":
          return !l.sourceCommunityLessonId;
        case "published":
          return l.communityPublishStatus === "approved";
        default:
          return true;
      }
    });
    return sortLessonLibrary(
      fuzzyFilter(byMode, search, (l) => [l.title, l.description, l.slug]),
      sortKey
    );
  }, [lessons, search, sortKey, modeFilter]);

  async function handleExport(scope: "all" | "lessons") {
    if (!bearer) return;
    setMenuOpen(false);
    setExporting(true);
    setError(null);
    try {
      const { bundle } = await getCurriculumExport(bearer, { scope });
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `ccs-curriculum-${scope}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }

  async function handleImportFile(file: File) {
    if (!bearer) return;
    setImporting(true);
    setError(null);
    try {
      const text = await file.text();
      const bundle = JSON.parse(text) as CurriculumBundleV1;
      if (
        !window.confirm(
          "Import this curriculum bundle? Existing lessons with the same slug will be updated (merge mode)."
        )
      ) {
        return;
      }
      await postCurriculumImport(bearer, { bundle, mode: "merge", confirm: true });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  }

  async function handleDelete(lesson: TeacherLessonRecord) {
    if (!bearer || deletingId) return;
    setRowMenuId(null);
    if (!window.confirm(buildSoftDeleteLessonConfirmMessage(lesson))) return;
    setDeletingId(lesson.id);
    setError(null);
    try {
      await deleteLibraryLesson(bearer, lesson.id);
      setLessons((rows) => rows.filter((r) => r.id !== lesson.id));
      onLibraryChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleMoveToDrafts(lesson: TeacherLessonRecord) {
    if (!bearer) return;
    setRowMenuId(null);
    setError(null);
    try {
      await patchTeacherLesson(bearer, undefined, lesson.id, { isDraft: true });
      setLessons((rows) => rows.filter((r) => r.id !== lesson.id));
      onLibraryChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not move lesson to drafts");
    }
  }

  async function handleMoveToLibrary(lesson: TeacherLessonRecord) {
    if (!bearer) return;
    setRowMenuId(null);
    setError(null);
    try {
      await patchTeacherLesson(bearer, undefined, lesson.id, { isDraft: false });
      setLessons((rows) => rows.filter((r) => r.id !== lesson.id));
      onLibraryChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not move lesson to library");
    }
  }

  async function handlePublish(lesson: TeacherLessonRecord) {
    if (!bearer) return;
    setRowMenuId(null);
    setError(null);
    try {
      await postSubmitLessonToCommunity(bearer, lesson.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Publish failed");
    }
  }

  async function handleUpgradeAll() {
    if (!bearer || upgradingAll) return;
    setMenuOpen(false);
    setUpgradingAll(true);
    setError(null);
    try {
      const { upgraded } = await postUpgradeAllLessons(bearer);
      setUpgradeReport(upgraded);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upgrade failed");
    } finally {
      setUpgradingAll(false);
    }
  }

  async function openInspiration(lesson: TeacherLessonRecord) {
    if (!bearer) return;
    setRowMenuId(null);
    setInspiration({ lessonId: lesson.id, info: null, loading: true, upgrading: false });
    try {
      const info = await getLessonInspiration(bearer, lesson.id);
      setInspiration({ lessonId: lesson.id, info, loading: false, upgrading: false });
    } catch {
      setInspiration({ lessonId: lesson.id, info: null, loading: false, upgrading: false });
    }
  }

  async function handleUpgradeOne() {
    if (!bearer || !inspiration) return;
    setInspiration((s) => (s ? { ...s, upgrading: true } : s));
    try {
      await postUpgradeLesson(bearer, inspiration.lessonId);
      setInspiration(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upgrade failed");
      setInspiration((s) => (s ? { ...s, upgrading: false } : s));
    }
  }

  function findAuthor(email: string) {
    setInspiration(null);
    navigate(`/teacher/lessons?tab=community&q=${encodeURIComponent(`author:${email}`)}`);
  }

  const busy = exporting || importing || upgradingAll;

  const actions = (
    <div className={`teacher-library-actions${embedded ? " teacher-embedded-actions" : ""}`}>
      <Link to="/teacher/lessons/new" className="btn primary">
        <FontAwesomeIcon icon={faPlus} /> New lesson
      </Link>
      <div className="teacher-actions-dropdown">
        <button
          ref={menuTriggerRef}
          type="button"
          className="btn secondary"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          disabled={busy}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {busy ? "Working…" : "Actions"} <FontAwesomeIcon icon={faCaretDown} />
        </button>
        <FloatingActionsMenu
          anchorRef={menuTriggerRef}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
        >
          <button
            type="button"
            role="menuitem"
            className="teacher-actions-menu-item"
            onClick={() => void handleUpgradeAll()}
          >
            <FontAwesomeIcon icon={faRotate} className="teacher-actions-menu-icon" />
            Upgrade inspired lessons
          </button>
          <button
            type="button"
            role="menuitem"
            className="teacher-actions-menu-item"
            onClick={() => {
              setMenuOpen(false);
              navigate("/teacher/lessons/deleted");
            }}
          >
            <FontAwesomeIcon icon={faTrash} className="teacher-actions-menu-icon" />
            See Recently Deleted Lessons
          </button>
          <div className="teacher-actions-menu-divider" />
          <button
            type="button"
            role="menuitem"
            className="teacher-actions-menu-item"
            onClick={() => {
              setMenuOpen(false);
              importInputRef.current?.click();
            }}
          >
            <FontAwesomeIcon icon={faFileImport} className="teacher-actions-menu-icon" />
            Import JSON
          </button>
          <button
            type="button"
            role="menuitem"
            className="teacher-actions-menu-item"
            onClick={() => void handleExport("lessons")}
          >
            <FontAwesomeIcon icon={faFileExport} className="teacher-actions-menu-icon" />
            Export lessons
          </button>
          <button
            type="button"
            role="menuitem"
            className="teacher-actions-menu-item"
            onClick={() => void handleExport("all")}
          >
            <FontAwesomeIcon icon={faFileExport} className="teacher-actions-menu-icon" />
            Export lessons and courses
          </button>
        </FloatingActionsMenu>
      </div>
      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        style={{ display: "none" }}
        disabled={importing}
        onChange={(ev) => {
          const f = ev.target.files?.[0];
          if (f) void handleImportFile(f);
        }}
      />
    </div>
  );

  const toolbar = (
    <div className="teacher-library-toolbar">
      <div className="teacher-library-search">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="teacher-library-search-icon" />
        <input
          className="input teacher-library-search-input"
          value={search}
          onChange={(ev) => setSearch(ev.target.value)}
          placeholder="Search lessons by title or description"
          type="search"
        />
      </div>
      <label className="teacher-library-sort">
        <span className="sr-only">Filter lessons</span>
          <select
            className="input"
            value={modeFilter}
            onChange={(ev) => setModeFilter(ev.target.value)}
            aria-label="Filter lessons"
          >
            <option value="all">All lessons</option>
            <option value="published">My published</option>
            <option value="simple">Simple only</option>
            <option value="advanced">Advanced only</option>
            <option value="inspired">Community inspired</option>
            <option value="authored">Personally authored</option>
          </select>
      </label>
      <label className="teacher-library-sort">
        <span className="sr-only">Sort lessons</span>
        <select
          className="input"
          value={sortKey}
          onChange={(ev) => setSortKey(ev.target.value)}
          aria-label="Sort lessons"
        >
          {LESSON_LIBRARY_SORT_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <div className="teacher-view-toggle" role="group" aria-label="View mode">
        <button
          type="button"
          className={`teacher-view-toggle-btn${viewMode === "cards" ? " is-active" : ""}`}
          title="Card view"
          aria-pressed={viewMode === "cards"}
          onClick={() => setView("cards")}
        >
          <FontAwesomeIcon icon={faTableCellsLarge} />
        </button>
        <button
          type="button"
          className={`teacher-view-toggle-btn${viewMode === "table" ? " is-active" : ""}`}
          title="Table view"
          aria-pressed={viewMode === "table"}
          onClick={() => setView("table")}
        >
          <FontAwesomeIcon icon={faTableList} />
        </button>
      </div>
    </div>
  );

  const updatePill = (lesson: TeacherLessonRecord) =>
    lesson.updateAvailable ? (
      <button
        type="button"
        className="teacher-pill teacher-pill--update"
        title="A newer version is available from the community"
        onClick={() => void openInspiration(lesson)}
      >
        Update available
      </button>
    ) : null;

  const body = (
    <>
      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      {embedded ? actions : null}
      {toolbar}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="muted">
          {lessons.length === 0
            ? scope === "drafts"
              ? "No drafts yet. AI-generated lessons stay here until you add them to a plan or save them to your library."
              : "No lessons yet. Use New lesson to make your first one."
            : "No lessons match your search or filters."}
        </p>
      ) : viewMode === "cards" ? (
        <div className="teacher-lesson-grid">
          {filtered.map((l) => (
            <LessonLibraryCard
              key={l.id}
              lesson={l}
              onPointerLeave={() => {
                if (rowMenuId === l.id) setRowMenuId(null);
              }}
              overlay={
                <>
                  {cardPublishedBadge(l.communityPublishStatus)}
                  {l.sourceCommunityLessonId ? (
                    <span className="teacher-pill teacher-pill--inspired">Inspired</span>
                  ) : null}
                  {updatePill(l)}
                </>
              }
              extraTools={
                <LessonRowMenu
                  lesson={l}
                  variant="card"
                  scope={scope}
                  open={rowMenuId === l.id}
                  onToggle={() => setRowMenuId((id) => (id === l.id ? null : l.id))}
                  onClose={() => setRowMenuId(null)}
                  onPublish={() => void handlePublish(l)}
                  onDelete={() => void handleDelete(l)}
                  onInspiration={() => void openInspiration(l)}
                  onMoveToDrafts={
                    scope === "library" ? () => void handleMoveToDrafts(l) : undefined
                  }
                  onMoveToLibrary={
                    scope === "drafts" ? () => void handleMoveToLibrary(l) : undefined
                  }
                />
              }
            />
          ))}
        </div>
      ) : (
        <div className="teacher-table-wrap">
          <table className="teacher-table teacher-lessons-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Version</th>
                <th>Templates</th>
                <th>Active cohorts</th>
                <th>Assigned</th>
                <th>Finished</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id}>
                  <td>
                    <Link to={`/teacher/lessons/${l.id}`} className="teacher-lesson-row-title">
                      <LessonIcon
                        lessonId={l.id}
                        title={l.title}
                        iconName={l.iconName}
                        bgColor={l.iconBgColor}
                        fgColor={l.iconFgColor}
                        workspaceMode={l.workspaceMode}
                        size={2.25}
                      />
                      <span>
                        <strong>{l.title}</strong>
                        <span style={{ display: "flex", gap: "0.3rem", marginTop: "0.2rem", flexWrap: "wrap" }}>
                          {l.sourceCommunityLessonId ? (
                            <span className="teacher-pill teacher-pill--inspired">Inspired</span>
                          ) : null}
                          {updatePill(l)}
                        </span>
                      </span>
                    </Link>
                  </td>
                  <td>{publishStatusLabel(l.communityPublishStatus) ?? <span className="muted">—</span>}</td>
                  <td className="muted">v{l.version ?? 1}</td>
                  <td className="muted">{l.usedInTemplates ?? 0}</td>
                  <td className="muted">{l.activeCohorts ?? 0}</td>
                  <td className="muted">{l.studentsAssigned ?? 0}</td>
                  <td className="muted">{l.studentsFinished ?? 0}</td>
                  <td className="teacher-table-actions teacher-lesson-row-actions">
                    <Link
                      to={`/teacher/lessons/${l.id}`}
                      className="btn primary tight teacher-icon-btn"
                      title="Edit lesson"
                      aria-label={`Edit ${l.title}`}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </Link>
                    <LessonRowMenu
                      lesson={l}
                      scope={scope}
                      open={rowMenuId === l.id}
                      onToggle={() => setRowMenuId((id) => (id === l.id ? null : l.id))}
                      onClose={() => setRowMenuId(null)}
                      onPublish={() => void handlePublish(l)}
                      onDelete={() => void handleDelete(l)}
                      onInspiration={() => void openInspiration(l)}
                      onMoveToDrafts={
                        scope === "library" ? () => void handleMoveToDrafts(l) : undefined
                      }
                      onMoveToLibrary={
                        scope === "drafts" ? () => void handleMoveToLibrary(l) : undefined
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InspirationModal
        open={inspiration !== null}
        noun="lesson"
        info={inspiration?.info ?? null}
        loading={inspiration?.loading}
        upgrading={inspiration?.upgrading}
        onClose={() => setInspiration(null)}
        onUpgrade={() => void handleUpgradeOne()}
        onFindAuthor={findAuthor}
      />

      {upgradeReport !== null ? (
        <div className="ccs-modal-overlay" role="dialog" aria-modal="true" aria-label="Upgrade results">
          <div className="teacher-modal-card">
            <h2 className="teacher-modal-title">Upgrade complete</h2>
            {upgradeReport.length === 0 ? (
              <p className="muted">All inspired lessons were already up to date.</p>
            ) : (
              <ul className="teacher-changelog-list">
                {upgradeReport.map((r) => (
                  <li key={r.lessonId} className="small">
                    <strong>{r.title}</strong> upgraded to v{r.toVersion}
                  </li>
                ))}
              </ul>
            )}
            <div className="teacher-modal-actions">
              <span style={{ flex: 1 }} />
              <button type="button" className="btn primary" onClick={() => setUpgradeReport(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );

  if (embedded) return body;

  return (
    <div className="teacher-page submissions-dashboard">
      <header className="submissions-hero overview-hero">
        <div className="submissions-hero-text">
          <p className="eyebrow">Teacher · Library</p>
          <h1 className="teacher-page-title">My lessons</h1>
          <p className="teacher-page-lede">
            Your lesson library. Search it, browse it, and add lessons to any course sequence.
          </p>
        </div>
        {actions}
      </header>
      {body}
    </div>
  );
}
